import { Application, ApplicationService } from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import { useError } from '@/hooks/useError'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import { useLocation } from 'react-router'

interface CertificateApplicationContextProps {
  application: Application
  reload: (background?: boolean) => Promise<void>
}

const CertificateApplicationContext =
  createContext<CertificateApplicationContextProps>(undefined!)

export function useCertificateApplication() {
  const context = use(CertificateApplicationContext)
  if (context === undefined) {
    throw new Error(
      'useCertificateApplication must be used within a CertificateApplicationProvider'
    )
  }

  return context
}

export interface CertificateApplicationProviderProps {
  applicationId: string
}

export function CertificateApplicationProvider({
  applicationId,
  children,
}: PropsWithChildren<CertificateApplicationProviderProps>) {
  const { showError } = useError()
  const { pathname } = useLocation()

  // State
  const [loading, setLoading] = useState(true)
  const [application, setApplication] = useState<Application | null>(null)

  // Load the application details
  const reload = useCallback(
    async (background?: boolean) => {
      if (!applicationId) return
      if (!background) setLoading(true)

      try {
        const application =
          await ApplicationService.getApplication(applicationId)
        setApplication(application)
      } catch (error) {
        showError(error, 'Failed to load application details.')
      } finally {
        setLoading(false)
      }
    },
    [applicationId, showError]
  )

  // Load application details when ID changes
  useEffect(() => {
    reload()
  }, [reload])

  // Every step page reads its slice of `application` from this one shared
  // fetch, so re-fetch in the background on every step navigation (without
  // the fullscreen loading state) — otherwise a page saved earlier in the
  // wizard (e.g. Business Structure) keeps showing pre-save data if the user
  // navigates back to it, or on Review/Submitted after editing a section.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    reload(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // No application yet, show loading
  if (loading)
    return <Loading variant="fullscreen" message="Retrieving application..." />
  if (!application) return <div>Application not found</div>

  return (
    <CertificateApplicationContext.Provider
      value={{
        application,
        reload,
      }}
    >
      {children}
    </CertificateApplicationContext.Provider>
  )
}
